"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { TabletIllustration } from "@/components/ui/TabletIllustration";

export interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
}

interface NavDropdownProps {
  isOpen: boolean;
  items: NavItem[];
  onClose: () => void;
}

export function NavDropdown({ isOpen, items, onClose }: NavDropdownProps) {
  const t = useTranslations("Navigation");
  const isClient = typeof window !== "undefined";

  useEffect(() => {
    const isTablet = () =>
      typeof window !== "undefined" &&
      window.innerWidth >= 768 &&
      window.innerWidth < 1536;

    if (isTablet() && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const tabletOverlay = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="tablet-fullscreen"
          variants={{
            hidden: { clipPath: "inset(0 0 100% 0)" },
            visible: {
              clipPath: "inset(0 0 0% 0)",
              transition: { duration: 0.52, ease: [0.16, 1, 0.3, 1] as const },
            },
            exit: {
              clipPath: "inset(0 0 100% 0)",
              transition: { duration: 0.38, ease: [0.7, 0, 0.84, 0] as const },
            },
          }}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="dialog"
          aria-modal="true"
          aria-label={t("aria_label")}
          className="fixed inset-0 z-40 bg-(--bg-primary) hidden md:flex flex-col 2xl:hidden"
        >
          <div className="h-20 shrink-0" />

          <div className="flex-1 flex overflow-hidden">
            <div className="w-1/2 flex flex-col justify-center px-8 md:px-10 relative overflow-hidden">
              <div className="absolute -left-20 top-1/2 -translate-y-1/2 w-64 h-64 bg-(--brand-primary)/5 rounded-full blur-[90px] pointer-events-none" />

              <motion.ul
                variants={{
                  hidden: {},
                  visible: {
                    transition: { staggerChildren: 0.05, delayChildren: 0.2 },
                  },
                  exit: {
                    transition: { staggerChildren: 0.02, staggerDirection: -1 },
                  },
                }}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="flex flex-col relative z-10"
              >
                {items.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.li
                      key={item.href}
                      variants={{
                        hidden: { opacity: 0, y: 28 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.44,
                            ease: [0.16, 1, 0.3, 1] as const,
                          },
                        },
                        exit: {
                          opacity: 0,
                          y: 16,
                          transition: {
                            duration: 0.2,
                            ease: [0.7, 0, 0.84, 0] as const,
                          },
                        },
                      }}
                    >
                      <a
                        href={item.href}
                        onClick={onClose}
                        className={cn(
                          "group flex items-center gap-4 py-2.5",
                          "border-b border-(--border-subtle) last:border-0",
                          "transition-colors duration-200",
                        )}
                      >
                        <span className="w-6 shrink-0 text-right text-[10px] font-mono font-semibold text-(--text-tertiary) group-hover:text-(--brand-primary) transition-colors duration-200">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <motion.div
                          whileHover={{ scale: 1.12, rotate: 6 }}
                          whileTap={{ scale: 1.18, rotate: 10 }}
                          transition={{
                            duration: 0.18,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          className={cn(
                            "w-8 h-8 rounded-lg shrink-0",
                            "flex items-center justify-center",
                            "border border-(--border-subtle) bg-(--bg-card) text-(--text-secondary)",
                            "group-hover:border-(--text-primary)/10 group-hover:text-(--text-primary)",
                            "transition-colors duration-300",
                          )}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </motion.div>
                        <span className="text-xl font-bold tracking-tight text-(--text-primary) group-hover:text-(--brand-primary) transition-colors duration-200 leading-none">
                          {item.label}
                        </span>
                        <ChevronRight
                          className={cn(
                            "ml-auto w-3.5 h-3.5 text-(--brand-primary)",
                            "opacity-0 -translate-x-2",
                            "transition-all duration-200",
                            "group-hover:opacity-100 group-hover:translate-x-0",
                          )}
                        />
                      </a>
                    </motion.li>
                  );
                })}
              </motion.ul>
            </div>

            <motion.div
              variants={{
                hidden: { opacity: 0, x: 32 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: {
                    duration: 0.55,
                    delay: 0.28,
                    ease: [0.16, 1, 0.3, 1] as const,
                  },
                },
                exit: {
                  opacity: 0,
                  x: 32,
                  transition: {
                    duration: 0.2,
                    ease: [0.7, 0, 0.84, 0] as const,
                  },
                },
              }}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="w-[45%] flex items-center justify-center border-l border-(--border-subtle) relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_50%_at_55%_50%,var(--brand-primary)_0%,transparent_70%)] opacity-[0.03] pointer-events-none" />
              <TabletIllustration />
            </motion.div>
          </div>

          <motion.div
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { delay: 0.5, duration: 0.3 },
              },
              exit: { opacity: 0, transition: { duration: 0.12 } },
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="shrink-0 border-t border-(--border-subtle) px-10 py-3 flex items-center justify-between"
          >
            <span className="text-[10px] font-mono text-(--text-tertiary) tracking-widest uppercase">
              &copy; {new Date().getFullYear()} devdj
            </span>
            <span className="text-[10px] font-mono text-(--text-tertiary) tracking-widest uppercase">
              {t("press_esc")}
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {isClient ? createPortal(tabletOverlay, document.body) : tabletOverlay}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-dropdown"
            variants={{
              hidden: { opacity: 0, y: -10 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.22,
                  ease: [0.16, 1, 0.3, 1] as const,
                },
              },
              exit: {
                opacity: 0,
                y: -10,
                transition: {
                  duration: 0.16,
                  ease: [0.7, 0, 0.84, 0] as const,
                },
              },
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute top-full left-0 right-0 bg-(--bg-primary) border-b border-(--border-subtle) p-4 md:hidden shadow-xl z-40"
          >
            <ul className="flex flex-col gap-1">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      onClick={onClose}
                      className={cn(
                        "group flex items-center gap-3 px-3 py-2 rounded-xl",
                        "hover:bg-(--bg-card) transition-colors duration-300 cursor-pointer pointer-events-auto",
                      )}
                    >
                      <motion.div
                        whileTap={{ scale: 1.2, rotate: 10 }}
                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                          "border border-(--border-subtle) bg-(--bg-card)/5 text-(--text-secondary)",
                          "group-hover:border-(--text-primary)/10 group-hover:text-(--text-primary)",
                          "transition-colors duration-300",
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </motion.div>
                      <span className="text-sm font-semibold text-(--text-primary) group-hover:text-(--brand-primary) transition-colors duration-200">
                        {item.label}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 pt-3 border-t border-(--border-subtle) flex items-center justify-between">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
